<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <h3 class="detail-page-title mb-0">{{ employee.name }}</h3>
          <div class="detail-page-actions">
            <button v-if="helper.checkAccess('edit_employees') != -1" @click="editEmployee" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
              {{$t('edit')}}
            </button>
            <button v-if="helper.checkAccess('delete_employees') != -1" @click="deleteEmployee" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--danger">
              <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
              {{$t('delete')}}
            </button>
            <button v-if="!employee.attached_user_id && helper.checkAccess('create_users') != -1" @click="openUserModal" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-success btn-xs">
              <AppIcon name="bi-person-plus" class="bi bi-person-plus" style="font-size:12px" />
              {{$t('create_user')}}
            </button>
            <button v-if="employee.attached_user_id" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-info btn-xs" disabled>
              <AppIcon name="bi-person-check" class="bi bi-person-check" style="font-size:12px" />
              {{$t('user_created')}}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Employee Info Cards -->
      <div v-else-if="employee && employee.id">

        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: activeTab === 'overview'}" @click="activeTab = 'overview'">{{$t('overview')}}</a>
          </li>

          <li class="nav-item" v-if="helper.checkAccess('view_holidays') != -1">
            <a class="nav-link" :class="{active: activeTab === 'leaves'}" @click="activeTab = 'leaves'">{{$t('leaves')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attendances') != -1">
            <a class="nav-link" :class="{active: activeTab === 'attendance'}" @click="activeTab = 'attendance'; fetchAttendances()">{{$t('attendance')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_payrolls') != -1">
            <a class="nav-link" :class="{active: activeTab === 'payrolls'}" @click="activeTab = 'payrolls'; fetchPayrolls()">{{$t('payrolls')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'">
          <div class="row">
            <!-- Left Column: Personal Details -->
            <div class="col-md-8">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-person" class="bi bi-person me-2" />{{$t('personal_details')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{employee.reference}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{employee.name}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{employee.email || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{employee.phone || '-'}}</strong>
                    </div>
                    <div class="col-md-12 mb-3">
                      <small class="text-muted d-block">{{$t('address')}}</small>
                      <strong>{{employee.address || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('date_of_birth')}}</small>
                      <strong>{{employee.date_of_birth ? formatDate(employee.date_of_birth) : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('gender')}}</small>
                      <strong>{{employee.gender ? $t(employee.gender) : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('nationality')}}</small>
                      <strong>{{employee.nationality || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('national_id')}}</small>
                      <strong>{{employee.national_id || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">N° CNSS</small>
                      <strong>{{employee.cnss_number || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">Personnes à charge</small>
                      <strong>{{employee.dependents_count || 0}}</strong>
                    </div>
                    <div class="col-md-12 mb-3">
                      <small class="text-muted d-block">{{$t('emergency_contact')}}</small>
                      <strong>{{employee.emergency_contact_name || '-'}} {{employee.emergency_contact_phone ? '(' + employee.emergency_contact_phone + ')' : ''}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Job Details Card -->
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-briefcase" class="bi bi-briefcase me-2" />{{$t('job_details')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('position')}}</small>
                      <strong>{{employee.position || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('department')}}</small>
                      <strong>{{employee.department || '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('employment_type')}}</small>
                      <strong>{{employee.employment_type ? $t(employee.employment_type) : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('hire_date')}}</small>
                      <strong>{{employee.hire_date ? formatDate(employee.hire_date) : '-'}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('salary')}}</small>
                      <strong>{{employee.salary ? formatMoney(employee.salary) : '-'}} {{employee.salary_currency || ''}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('work_schedule')}}</small>
                      <strong>{{employee.work_schedule || '-'}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes Card -->
              <div v-if="employee.notes" class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ employee.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Quick Info -->
            <div class="col-md-4">
              <!-- Leave Balance Card -->
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-calendar-check" class="bi bi-calendar-check me-2" />{{$t('leave_balance')}}</h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('annual_leave_balance')}}</small>
                    <h4 class="text-primary mb-0">{{employee.annual_leave_balance || 0}} <small>{{$t('days')}}</small></h4>
                  </div>
                  <div>
                    <small class="text-muted d-block">{{$t('sick_leave_balance')}}</small>
                    <h4 class="text-warning mb-0">{{employee.sick_leave_balance || 0}} <small>{{$t('days')}}</small></h4>
                  </div>
                </div>
              </div>

              <!-- Status Card -->
              <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-info-circle" class="bi bi-info-circle me-2" />{{$t('employment_status')}}</h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('current_status')}}</small>
                    <span class="badge" :class="{
                      'bg-success': employee.status === 'active',
                      'bg-secondary': employee.status === 'inactive',
                      'bg-warning': employee.status === 'on_leave',
                      'bg-danger': employee.status === 'terminated'
                    }">
                      {{$t(employee.status)}}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>



        <!-- Leaves Tab -->
        <div v-if="activeTab === 'leaves'" class="card">
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-hover table-striped small">
                <thead>
                  <tr class="text-secondary">
                    <th>{{$t('leave_type')}}</th>
                    <th>{{$t('start_date')}}</th>
                    <th>{{$t('end_date')}}</th>
                    <th>{{$t('days')}}</th>
                    <th>{{$t('reason')}}</th>
                    <th>{{$t('status')}}</th>
                    <th>{{$t('action')}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="leave in leaves" :key="leave.id">
                    <td>{{$t(leave.leave_type)}}</td>
                    <td>{{formatDate(leave.start_date)}}</td>
                    <td>{{formatDate(leave.end_date)}}</td>
                    <td>{{leave.days_count}}</td>
                    <td>{{leave.reason || '-'}}</td>
                    <td>
                      <span class="badge" :class="{
                        'bg-warning': leave.status === 'pending',
                        'bg-success': leave.status === 'approved',
                        'bg-danger': leave.status === 'rejected',
                        'bg-secondary': leave.status === 'cancelled'
                      }">
                        {{$t(leave.status)}}
                      </span>
                    </td>
                    <td>
                      <button v-if="leave.status === 'pending' && helper.checkAccess('edit_holidays') != -1" @click="approveLeave(leave)" class="btn btn-xs btn-success me-1" style="padding: 5px;font-size: 12px">
                        <AppIcon name="bi-check" class="bi bi-check" /> {{$t('approve')}}
                      </button>
                      <button v-if="leave.status === 'pending' && helper.checkAccess('edit_holidays') != -1" @click="rejectLeave(leave)" class="btn btn-xs btn-danger" style="padding: 5px;font-size: 12px">
                        <AppIcon name="bi-x" class="bi bi-x" /> {{$t('reject')}}
                      </button>
                    </td>
                  </tr>
                  <tr v-if="leaves.length === 0">
                    <td colspan="7" class="text-center text-muted">{{$t('no_leave_requests')}}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Attendance Tab -->
        <div v-if="activeTab === 'attendance'" class="card">
          <div class="card-body">

            <!-- Quick Stats -->
            <div class="row mb-3 g-2">
              <div class="col-md-3 col-6">
                <div class="card text-center">
                  <div class="card-body py-2 px-2">
                    <small class="text-muted d-block" style="font-size: 0.7rem">{{$t('this_month')}}</small>
                    <h5 class="text-success mb-1">{{attendanceStats.present || 0}}</h5>
                    <small class="text-muted" style="font-size: 0.75rem">{{$t('present')}}</small>
                  </div>
                </div>
              </div>
              <div class="col-md-3 col-6">
                <div class="card text-center">
                  <div class="card-body py-2 px-2">
                    <small class="text-muted d-block" style="font-size: 0.7rem">{{$t('this_month')}}</small>
                    <h5 class="text-danger mb-1">{{attendanceStats.absent || 0}}</h5>
                    <small class="text-muted" style="font-size: 0.75rem">{{$t('absent')}}</small>
                  </div>
                </div>
              </div>
              <div class="col-md-3 col-6">
                <div class="card text-center">
                  <div class="card-body py-2 px-2">
                    <small class="text-muted d-block" style="font-size: 0.7rem">{{$t('this_month')}}</small>
                    <h5 class="text-warning mb-1">{{attendanceStats.late || 0}}</h5>
                    <small class="text-muted" style="font-size: 0.75rem">{{$t('late')}}</small>
                  </div>
                </div>
              </div>
              <div class="col-md-3 col-6">
                <div class="card text-center">
                  <div class="card-body py-2 px-2">
                    <small class="text-muted d-block" style="font-size: 0.7rem">{{$t('avg_hours')}}</small>
                    <h5 class="text-primary mb-1">{{attendanceStats.avgHours || 0}}</h5>
                    <small class="text-muted" style="font-size: 0.75rem">{{$t('hours')}}</small>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-hover table-striped small">
                <thead>
                  <tr class="text-secondary">
                    <th>{{$t('date')}}</th>
                    <th>{{$t('check_in')}}</th>
                    <th>{{$t('check_out')}}</th>
                    <th>{{$t('work_hours')}}</th>
                    <th>{{$t('status')}}</th>
                    <th>{{$t('notes')}}</th>
                    <th>{{$t('action')}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="att in attendances" :key="att.id">
                    <td>{{formatDate(att.date)}}</td>
                    <td>{{att.check_in ? formatTime(att.check_in) : '-'}}</td>
                    <td>{{att.check_out ? formatTime(att.check_out) : '-'}}</td>
                    <td>{{att.work_hours || '-'}} {{att.work_hours ? 'hrs' : ''}}</td>
                    <td>
                      <span class="badge" :class="{
                        'bg-success': att.status === 'present',
                        'bg-danger': att.status === 'absent',
                        'bg-warning': att.status === 'late',
                        'bg-info': att.status === 'half_day',
                        'bg-secondary': att.status === 'on_leave'
                      }">
                        {{$t(att.status)}}
                      </span>
                    </td>
                    <td>{{att.notes || '-'}}</td>
                    <td>
                      <button v-if="helper.checkAccess('edit_attendances') != -1" @click="editAttendance(att)" class="btn btn-xs btn-primary me-1" style="padding: 5px;font-size: 12px">
                        <AppIcon name="bi-pencil" class="bi bi-pencil" />
                      </button>
                      <button v-if="helper.checkAccess('delete_attendances') != -1" @click="deleteAttendance(att)" class="btn btn-xs btn-danger" style="padding: 5px;font-size: 12px">
                        <AppIcon name="bi-trash" class="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                  <tr v-if="attendances.length === 0">
                    <td colspan="7" class="text-center text-muted">{{$t('no_attendance_records')}}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create User Modal -->
    <div class="modal fade" id="userCreateModal" tabindex="-1" aria-labelledby="userCreateModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="userCreateModalLabel">{{$t('create_user')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-sm-3 mb-3">
                <label class="form-label">{{$t('name')}}</label>
                <input v-model="userForm.name" type="text" class="form-control" :placeholder="$t('name')">
              </div>
              <div class="col-sm-5 mb-3">
                <label class="form-label">{{$t('email')}}</label>
                <input v-model="userForm.email" type="email" class="form-control" :placeholder="$t('email')">
              </div>
              <div class="col-4 mb-3">
                <label class="form-label">{{$t('password')}}</label>
                <div class="input-group">
                  <input v-model="userForm.password" :type="showPassword ? 'text' : 'password'" class="form-control" placeholder="********">
                  <button class="btn btn-outline-secondary" type="button" @click="showPassword = !showPassword">
                    <AppIcon :name="showPassword ? 'bi-eye-slash' : 'bi-eye'" class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'" />
                  </button>
                </div>
              </div>
              <div class="col-12 mb-3">
                <label class="form-label">{{$t('access')}}</label>
                <div class="form-check mb-2">
                  <input v-model="userForm.role" class="form-check-input" type="radio" id="role_manage_all" value="admin">
                  <label class="form-check-label fw-bold" for="role_manage_all">
                    {{$t('full_access')}}
                  </label>
                  <div class="text-muted small ms-1">
                    {{$t('full_access_description')}}
                  </div>
                </div>
                <div class="form-check">
                  <input v-model="userForm.role" class="form-check-input" type="radio" id="role_manage_created" value="staff">
                  <label class="form-check-label fw-bold" for="role_manage_created">
                    {{$t('limited_access')}}
                  </label>
                  <div class="text-muted small ms-1">
                    {{$t('limited_access_description')}}
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold text-primary mb-3">
                  <AppIcon name="bi-shield-lock-fill" class="bi bi-shield-lock-fill me-2" />{{$t('permissions')}}
                </label>

                <div class="row g-3">
                  <div v-for="module in permissionsModules" :key="module.id" class="col-sm-6 col-lg-4">
                    <div class="card border-0 shadow-sm h-100 hover-shadow">
                      <div class="card-header bg-primary text-white py-3 d-flex align-items-center justify-content-between">
                        <div class="d-flex align-items-center">
                          <AppIcon :name="module.icon || 'bi bi-folder'" :class="module.icon || 'bi bi-folder'" class="text-white me-2" style="font-size: 1.2rem;" />
                          <h6 class="mb-0 text-white">{{$t(module.title)}}</h6>
                        </div>
                        <button type="button" class="btn btn-sm btn-light btn-toggle-module" @click="toggleModulePermissions(module)" :title="$t('toggle_all')">
                          <AppIcon name="bi-check2-square" class="bi bi-check2-square" />
                        </button>
                      </div>
                      <div class="card-body p-3">
                        <div v-for="item in module.items" :key="Object.keys(item)[0]" class="mb-3">
                          <div class="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
                            <div class="text-dark fw-semibold small">
                              <AppIcon name="bi-dot" class="bi bi-dot text-primary" />{{$t(Object.keys(item)[0])}}
                            </div>
                            <button type="button" class="btn btn-sm btn-link p-0 text-decoration-none" @click="toggleSubModulePermissions(Object.values(item)[0])" :title="$t('toggle_all')">
                              <AppIcon name="bi-check-circle" class="bi bi-check-circle text-success" style="font-size: 0.9rem;" />
                            </button>
                          </div>
                          <div class="ps-3">
                            <div v-for="permission in Object.values(item)[0].permissions" :key="permission.key" class="form-check mb-2">
                              <input v-model="permissionsState[permission.key]" class="form-check-input" type="checkbox" :id="`create-${permission.key}`">
                              <label class="form-check-label small" :for="`create-${permission.key}`">
                                {{$t(permission.label)}}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{$t('close')}}</button>
            <button type="button" class="btn btn-success" :disabled="loading || !userForm.name || !userForm.email || !userForm.password" @click="saveUser">
              <AppIcon name="bi-check-circle" class="bi bi-check-circle me-2" />{{$t('create')}}
            </button>
          </div>
        </div>
      </div>
    </div>



    <!-- Attendance Modal -->
    <div class="modal fade" id="attendanceModal" tabindex="-1" aria-labelledby="attendanceModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="attendanceModalLabel">{{attendanceForm.id ? $t('edit_attendance') : $t('record_attendance')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">{{$t('date')}} <span class="text-danger">*</span></label>
              <input type="date" v-model="attendanceForm.date" class="form-control">
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label">{{$t('check_in')}}</label>
                <input type="time" v-model="attendanceForm.check_in" @change="calculateWorkHours" class="form-control">
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label">{{$t('check_out')}}</label>
                <input type="time" v-model="attendanceForm.check_out" @change="calculateWorkHours" class="form-control">
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('work_hours')}}</label>
              <input type="number" step="0.5" v-model="attendanceForm.work_hours" class="form-control" readonly>
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('status')}} <span class="text-danger">*</span></label>
              <select v-model="attendanceForm.status" class="form-select">
                <option value="present">{{$t('present')}}</option>
                <option value="absent">{{$t('absent')}}</option>
                <option value="late">{{$t('late')}}</option>
                <option value="half_day">{{$t('half_day')}}</option>
                <option value="on_leave">{{$t('on_leave')}}</option>
              </select>
            </div>

            <div class="mb-3">
              <collapsible-notes-field v-model="attendanceForm.notes" id="employee-attendance-notes" rows="2" :placeholder="$t('enter_notes')" />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{$t('cancel')}}</button>
            <button type="button" class="btn btn-primary" @click="saveAttendance">{{$t('save')}}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Leave Request Modal -->
    <div class="modal fade" id="leaveModal" tabindex="-1" aria-labelledby="leaveModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="leaveModalLabel">{{$t('request_leave')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">{{$t('leave_type')}} <span class="text-danger">*</span></label>
              <select v-model="leaveForm.leave_type" class="form-select">
                <option value="annual">{{$t('annual_leave')}}</option>
                <option value="sick">{{$t('sick_leave')}}</option>
                <option value="unpaid">{{$t('unpaid_leave')}}</option>
                <option value="maternity">{{$t('maternity_leave')}}</option>
                <option value="paternity">{{$t('paternity_leave')}}</option>
                <option value="compassionate">{{$t('compassionate_leave')}}</option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('start_date')}} <span class="text-danger">*</span></label>
              <input type="date" v-model="leaveForm.start_date" @change="calculateDays" class="form-control">
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('end_date')}} <span class="text-danger">*</span></label>
              <input type="date" v-model="leaveForm.end_date" @change="calculateDays" class="form-control">
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('total_days')}}</label>
              <input type="number" min="0.5" step="0.5" v-model.number="leaveForm.days_count" class="form-control">
              <small class="text-muted">Jours de travail effectif: exclure le repos hebdomadaire et les jours fériés payés.</small>
            </div>

            <div class="mb-3">
              <label class="form-label">{{$t('reason')}}</label>
              <textarea v-model="leaveForm.reason" class="form-control" rows="3" :placeholder="$t('enter_reason')"></textarea>
            </div>

            <div class="alert alert-info small">
              <AppIcon name="bi-info-circle" class="bi bi-info-circle" />
              {{$t('annual_leave_balance')}}: <strong>{{employee.annual_leave_balance || 0}}</strong> {{$t('days')}} |
              {{$t('sick_leave_balance')}}: <strong>{{employee.sick_leave_balance || 0}}</strong> {{$t('days')}}
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">{{$t('cancel')}}</button>
            <button type="button" class="btn btn-primary" @click="saveLeave">{{$t('submit_request')}}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Payrolls Tab -->
    <div v-if="activeTab === 'payrolls'" class="card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5>{{$t('payrolls')}}</h5>
        </div>

        <!-- Loading State -->
        <table-loading-bar v-if="payrollsLoading" class="text-center py-5" />

        <!-- Payrolls Table -->
        <div v-if="!payrollsLoading" class="table-responsive">
          <table class="table table-hover table-striped small">
            <thead>
              <tr class="text-secondary">
                <th>{{$t('period')}}</th>
                <th>{{$t('basic_salary')}}</th>
                <th>{{$t('gross_salary')}}</th>
                <th>{{$t('net_salary')}}</th>
                <th>{{$t('status')}}</th>
                <th>{{$t('payment_method')}}</th>
                <th>{{$t('payment_date')}}</th>
                <th>{{$t('action')}}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payroll in payrolls" :key="payroll.id">
                <td>{{formatPeriod(payroll.month)}}</td>
                <td>{{formatMoney(payroll.basic_salary)}}</td>
                <td>{{formatMoney(payroll.gross_salary)}}</td>
                <td>
                  <router-link :to="`/hr/payroll/${payroll.id}`" class="fw-bold text-primary text-decoration-none">
                    {{formatMoney(payroll.net_salary)}}
                  </router-link>
                </td>
                <td>
                  <span class="badge rounded-pill" :class="{
                    'bg-success': payroll.status === 'paid',
                    'bg-secondary': payroll.status === 'draft'
                  }">
                    {{$t(payroll.status)}}
                  </span>
                </td>
                <td>{{payroll.payment_method ? $t(payroll.payment_method) : '-'}}</td>
                <td>{{payroll.payment_date ? formatDate(payroll.payment_date) : '-'}}</td>
                <td>
                  <button @click="$router.push(`/hr/payroll/${payroll.id}`)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                    <AppIcon name="bi-eye" class="bi bi-eye" />
                    {{$t('details')}}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="!payrollsLoading && !payrolls.length" class="text-center py-5 text-muted">
          <AppIcon name="bi-wallet2" class="bi bi-wallet2 fs-1 d-block mb-2" />
          {{$t('no_payrolls_found')}}
        </div>
      </div>
    </div>


  </div>
</template>

<script>
import helper from '@/helper'


export default {
  name: 'EmployeeDetails',
  components: {

  },
  data() {
    return {
      helper,
      employee: {},
      activeTab: 'overview',
      loading: true,
      leaves: [],
      leaveForm: {
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: '',
        days_count: 0
      },
      leaveModal: null,
      attendances: [],
      attendanceForm: {
        id: null,
        date: '',
        check_in: '',
        check_out: '',
        work_hours: 0,
        status: 'present',
        notes: ''
      },
      attendanceModal: null,
      attendanceStats: {
        present: 0,
        absent: 0,
        late: 0,
        avgHours: 0
      },

      payrolls: [],
      payrollsLoading: false,
      userForm: {
        name: '',
        email: '',
        password: '',
        role: 'staff'
      },
      userModal: null,
      showPassword: false,
      permissionsState: {},
      permissionsModules: [
        {
          id: 'dashboard',
          title: 'dashboard_title',
          icon: 'bi bi-speedometer2',
          items: [
            {
              dashboard: {
                permissions: [
                  { key: 'view_dashboard', label: 'view_dashboard' },
                ]
              }
            }
          ]
        },
        {
          id: 'crm',
          title: 'crm_title',
          icon: 'bi bi-people-fill',
          items: [
            {
              clients: {
                permissions: [
                  { key: 'create_clients', label: 'create_clients' },
                  { key: 'edit_clients', label: 'edit_clients' },
                  { key: 'view_clients', label: 'view_clients' },
                  { key: 'delete_clients', label: 'delete_clients' }
                ]
              }
            },
            {
              suppliers: {
                permissions: [
                  { key: 'create_suppliers', label: 'create_suppliers' },
                  { key: 'edit_suppliers', label: 'edit_suppliers' },
                  { key: 'view_suppliers', label: 'view_suppliers' },
                  { key: 'delete_suppliers', label: 'delete_suppliers' }
                ]
              }
            }
          ]
        },
        {
          id: 'inventory',
          title: 'inventory_title',
          icon: 'bi bi-box-seam',
          items: [
            {
              products: {
                permissions: [
                  { key: 'create_inventories', label: 'create_inventories' },
                  { key: 'edit_inventories', label: 'edit_inventories' },
                  { key: 'view_inventories', label: 'view_inventories' },
                  { key: 'delete_inventories', label: 'delete_inventories' }
                ]
              }
            },
            {
              categories: {
                permissions: [
                  { key: 'create_categories', label: 'create_categories' },
                  { key: 'edit_categories', label: 'edit_categories' },
                  { key: 'view_categories', label: 'view_categories' },
                  { key: 'delete_categories', label: 'delete_categories' }
                ]
              }
            },
            {
              warehouses: {
                permissions: [
                  { key: 'create_warehouses', label: 'create_warehouses' },
                  { key: 'edit_warehouses', label: 'edit_warehouses' },
                  { key: 'view_warehouses', label: 'view_warehouses' },
                  { key: 'delete_warehouses', label: 'delete_warehouses' }
                ]
              }
            }
          ]
        },
        {
          id: 'sales',
          title: 'sales_title',
          icon: 'bi bi-cart-check-fill',
          items: [
            {
              quote: {
                permissions: [
                  { key: 'create_quote', label: 'create_quote' },
                  { key: 'edit_quote', label: 'edit_quote' },
                  { key: 'view_quotes', label: 'view_quotes' },
                  { key: 'delete_quote', label: 'delete_quote' }
                ]
              }
            },
            {
              orders: {
                permissions: [
                  { key: 'create_orders', label: 'create_orders' },
                  { key: 'edit_orders', label: 'edit_orders' },
                  { key: 'view_orders', label: 'view_orders' },
                  { key: 'delete_orders', label: 'delete_orders' }
                ]
              }
            },
            {
              deliveries: {
                permissions: [
                  { key: 'create_deliveries', label: 'create_deliveries' },
                  { key: 'edit_deliveries', label: 'edit_deliveries' },
                  { key: 'view_deliveries', label: 'view_deliveries' },
                  { key: 'delete_deliveries', label: 'delete_deliveries' }
                ]
              }
            },
            {
              invoices: {
                permissions: [
                  { key: 'create_invoices', label: 'create_invoices' },
                  { key: 'edit_invoices', label: 'edit_invoices' },
                  { key: 'view_invoices', label: 'view_invoices' },
                  { key: 'delete_invoices', label: 'delete_invoices' }
                ]
              }
            },
            {
              processes: {
                permissions: [
                  { key: 'create_receipts', label: 'create_receipts' },
                  { key: 'edit_receipts', label: 'edit_receipts' },
                  { key: 'view_receipts', label: 'view_receipts' },
                  { key: 'delete_receipts', label: 'delete_receipts' }
                ]
              }
            },
            {
              returns: {
                permissions: [
                  { key: 'create_returns', label: 'create_returns' },
                  { key: 'edit_returns', label: 'edit_returns' },
                  { key: 'view_returns', label: 'view_returns' },
                  { key: 'delete_returns', label: 'delete_returns' }
                ]
              }
            }
          ]
        },
        {
          id: 'purchases',
          title: 'purchasing_title',
          icon: 'bi bi-bag-check-fill',
          items: [
            {
              purchases: {
                permissions: [
                  { key: 'create_purchases', label: 'create_purchases' },
                  { key: 'edit_purchases', label: 'edit_purchases' },
                  { key: 'view_purchases', label: 'view_purchases' },
                  { key: 'delete_purchases', label: 'delete_purchases' }
                ]
              }
            },
            {
              receptions: {
                permissions: [
                  { key: 'create_receptions', label: 'create_receptions' },
                  { key: 'edit_receptions', label: 'edit_receptions' },
                  { key: 'view_receptions', label: 'view_receptions' },
                  { key: 'delete_receptions', label: 'delete_receptions' }
                ]
              }
            },
            {
              "supplier_invoices": {
                permissions: [
                  { key: 'create_supplier_invoices', label: 'create_supplier_invoices' },
                  { key: 'edit_supplier_invoices', label: 'edit_supplier_invoices' },
                  { key: 'view_supplier_invoices', label: 'view_supplier_invoices' },
                  { key: 'delete_supplier_invoices', label: 'delete_supplier_invoices' }
                ]
              }
            },
            {
              "supplier_returns": {
                permissions: [
                  { key: 'create_supplier_returns', label: 'create_supplier_returns' },
                  { key: 'edit_supplier_returns', label: 'edit_supplier_returns' },
                  { key: 'view_supplier_returns', label: 'view_supplier_returns' },
                  { key: 'delete_supplier_returns', label: 'delete_supplier_returns' }
                ]
              }
            }
          ]
        },
        {
          id: 'pos',
          title: 'pos_title',
          icon: 'bi bi-cash-stack',
          items: [
            {
              pos: {
                permissions: [
                  { key: 'create_pos', label: 'create_pos' },
                  { key: 'view_pos', label: 'view_pos' },
                ]
              }
            },
            {
              "transaction": {
                permissions: [
                  { key: 'view_pos_transactions', label: 'view_pos_transactions' },
                  { key: 'delete_pos_transactions', label: 'delete_pos_transactions' }
                ]
              }
            },
            {
              "cash_register": {
                permissions: [
                  { key: 'view_pos_cash_registers', label: 'view_pos_cash_registers' },
                ]
              }
            },
            {
              "pos_insights": {
                permissions: [
                  { key: 'view_pos_insights', label: 'pos_insights' },
                ]
              }
            },
          ]
        },
        {
          id: 'finance',
          title: 'finance_title',
          icon: 'bi bi-wallet2',
          items: [
            {
              payments: {
                permissions: [
                  { key: 'create_payments', label: 'create_payments' },
                  { key: 'view_payments', label: 'view_payments' },
                  { key: 'delete_payments', label: 'delete_payments' }
                ]
              }
            }
          ]
        },
        {
          id: 'attachments',
          title: 'attachments_title',
          icon: 'bi bi-paperclip',
          items: [
            {
              attachments: {
                permissions: [
                  { key: 'create_attachments', label: 'create_attachments' },
                  { key: 'edit_attachments', label: 'edit_attachments' },
                  { key: 'view_attachments', label: 'view_attachments' },
                  { key: 'delete_attachments', label: 'delete_attachments' }
                ]
              }
            }
          ]
        },
        {
          id: 'accountants',
          title: 'accountant',
          icon: 'bi bi-calculator',
          items: [
            {
              accountants: {
                permissions: [
                  { key: 'view_accountants', label: 'view_accountant' }
                ]
              }
            }
          ]
        },
        {
          id: 'project_management',
          title: 'project_management',
          icon: 'bi bi-kanban',
          items: [
            {
              projects: {
                permissions: [
                  { key: 'create_projects', label: 'create_projects' },
                  { key: 'edit_projects', label: 'edit_projects' },
                  { key: 'view_projects', label: 'view_projects' },
                  { key: 'delete_projects', label: 'delete_projects' }
                ]
              }
            },
            {
              tasks: {
                permissions: [
                  { key: 'create_tasks', label: 'create_tasks' },
                  { key: 'edit_tasks', label: 'edit_tasks' },
                  { key: 'view_tasks', label: 'view_tasks' },
                  { key: 'delete_tasks', label: 'delete_tasks' }
                ]
              }
            }
          ]
        },
        {
          id: 'planning',
          title: 'planning',
          icon: 'bi bi-calendar-check',
          items: [
            {
              plannings: {
                permissions: [
                  { key: 'create_plannings', label: 'create_plannings' },
                  { key: 'edit_plannings', label: 'edit_plannings' },
                  { key: 'view_plannings', label: 'view_plannings' },
                  { key: 'delete_plannings', label: 'delete_plannings' }
                ]
              }
            },
            {
              my_planning: {
                permissions: [
                  { key: 'view_my_planning', label: 'view_my_planning' }
                ]
              }
            }
          ]
        },
        {
          id: 'hr',
          title: 'hr_title',
          icon: 'bi bi-people',
          items: [
            {
              employees: {
                permissions: [
                  { key: 'create_employees', label: 'create_employees' },
                  { key: 'edit_employees', label: 'edit_employees' },
                  { key: 'view_employees', label: 'view_employees' },
                  { key: 'delete_employees', label: 'delete_employees' }
                ]
              }
            },
            {
              payrolls: {
                permissions: [
                  { key: 'create_payrolls', label: 'create_payrolls' },
                  { key: 'edit_payrolls', label: 'edit_payrolls' },
                  { key: 'view_payrolls', label: 'view_payrolls' },
                  { key: 'delete_payrolls', label: 'delete_payrolls' }
                ]
              }
            },
            {
              holidays: {
                permissions: [
                  { key: 'create_holidays', label: 'create_holidays' },
                  { key: 'edit_holidays', label: 'edit_holidays' },
                  { key: 'view_holidays', label: 'view_holidays' },
                  { key: 'delete_holidays', label: 'delete_holidays' }
                ]
              }
            },
            {
              attendances: {
                permissions: [
                  { key: 'create_attendances', label: 'create_attendances' },
                  { key: 'edit_attendances', label: 'edit_attendances' },
                  { key: 'view_attendances', label: 'view_attendances' },
                  { key: 'delete_attendances', label: 'delete_attendances' }
                ]
              }
            }
            // My Space is hidden from HR permissions for now.
            // {
            //   my_space: {
            //     permissions: [
            //       { key: 'view_my_space', label: 'view_my_space' }
            //     ]
            //   }
            // }
          ]
        }
      ]
    }
  },
  computed: {
    isHrAdmin() {
      const role = this.$store.getters.ROLE
      if (role === 'owner') return true
      const permissions = this.$store.getters.PERMISSIONS || []
      return permissions.includes('edit_employees') || permissions.includes('create_employees')
    },

  },
  mounted() {
    //check permissions
    if(helper.checkAccess('view_employees') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_employees') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchEmployee()
    if(helper.checkAccess('view_holidays') != -1) this.fetchLeaves()
    if(helper.checkAccess('view_attendances') != -1) this.fetchAttendances()

    // eslint-disable-next-line no-undef
    this.leaveModal = new bootstrap.Modal(document.getElementById('leaveModal'), {backdrop: 'static', keyboard: false})
    // eslint-disable-next-line no-undef
    this.attendanceModal = new bootstrap.Modal(document.getElementById('attendanceModal'), {backdrop: 'static', keyboard: false})
    // eslint-disable-next-line no-undef

    // eslint-disable-next-line no-undef
    this.userModal = new bootstrap.Modal(document.getElementById('userCreateModal'), {backdrop: 'static', keyboard: false})
  },
  methods: {
    formatDate(date) {
      return helper.formatDate(date)
    },
    formatMoney(amount) {
      return helper.formatMoney(amount)
    },
    formatTime(datetime) {
      if (!datetime) return '-'
      const date = new Date(datetime)
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
    },
    async fetchEmployee() {
      try {
        this.loading = true
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employees/${this.$route.params.id}`)

        // The helper returns axios response, so data is in response.data
        const data = response.data

        // Handle both response formats
        if (data.employee) {
          this.employee = data.employee
        } else if (data.id) {
          this.employee = data
        } else {
          throw new Error('Invalid response format')
        }

        this.loading = false
      } catch (error) {
        alert(error)
        this.loading = false
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message || 'Failed to load employee'
        })
      }
    },
    editEmployee() {
      // Open the employee modal from parent component
      // Navigate back to employees list and trigger edit
      this.$router.push({
        name: 'Employees',
        params: { editId: this.employee.id }
      })
    },
    async deleteEmployee() {
      const result = await this.$swal({
        title: this.$t('are_you_sure'),
        text: this.$t('you_wont_be_able_to_revert_this'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('yes_delete_it'),
        cancelButtonText: this.$t('cancel')
      })

      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/employees/${this.employee.id}`)
          this.$router.push('/hr/employees')
        } catch (error) {
          this.$swal({ icon: 'error', title: this.$t('error'), text: error.message })
        }
      }
    },
    async fetchLeaves() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employee-leaves?employee_id=${this.$route.params.id}`)
        this.leaves = response.data.data || []
      } catch (error) {
        alert(error)
      }
    },
    openLeaveModal() {
      this.leaveForm = {
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        reason: '',
        days_count: 0
      }
      this.leaveModal.show()
    },
    calculateDays() {
      if (this.leaveForm.start_date && this.leaveForm.end_date) {
        const start = new Date(this.leaveForm.start_date)
        const end = new Date(this.leaveForm.end_date)
        if (end < start) {
          this.leaveForm.days_count = 0
          return
        }
        const diffTime = end - start
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
        this.leaveForm.days_count = diffDays
      }
    },
    async saveLeave() {
      try {
        if (!this.leaveForm.start_date || !this.leaveForm.end_date) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: this.$t('please_fill_required_fields')
          })
          return
        }

        if (!Number(this.leaveForm.days_count)) this.calculateDays()
        if (this.leaveForm.days_count <= 0) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: 'La date de fin doit être postérieure ou égale à la date de début.'
          })
          return
        }

        const data = {
          employee_id: this.$route.params.id,
          leave_type: this.leaveForm.leave_type,
          start_date: this.leaveForm.start_date,
          end_date: this.leaveForm.end_date,
          days_count: this.leaveForm.days_count,
          reason: this.leaveForm.reason,
          status: 'pending'
        }

        await helper.customPost(`${process.env.VUE_APP_API}/employee-leaves`, data)
        this.leaveModal.hide()
        this.fetchLeaves()
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message || 'Failed to save leave request'
        })
      }
    },
    async approveLeave(leave) {
      const result = await this.$swal({
        title: this.$t('approve_leave'),
        text: `${this.$t('approve_leave_for')} ${leave.days_count} ${this.$t('days')}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('approve'),
        cancelButtonText: this.$t('cancel')
      })

      if (result.isConfirmed) {
        try {
          await helper.customPost(`${process.env.VUE_APP_API}/employee-leaves/${leave.id}/approve`, {})
          this.fetchLeaves()
          this.fetchEmployee() // Refresh to update leave balance
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: error.message
          })
        }
      }
    },
    async rejectLeave(leave) {
      const { value: reason } = await this.$swal({
        title: this.$t('reject_leave'),
        input: 'textarea',
        inputLabel: this.$t('rejection_reason'),
        inputPlaceholder: this.$t('enter_reason'),
        showCancelButton: true,
        confirmButtonText: this.$t('reject'),
        cancelButtonText: this.$t('cancel')
      })

      if (reason) {
        try {
          await helper.customPost(`${process.env.VUE_APP_API}/employee-leaves/${leave.id}/reject`, {
            rejection_reason: reason
          })
          this.fetchLeaves()
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: error.message
          })
        }
      }
    },
    async fetchAttendances() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employee-attendances?employee_id=${this.$route.params.id}`)
        this.attendances = response.data.data || []
        this.calculateAttendanceStats()
      } catch (error) {
        alert(error)
      }
    },
    calculateAttendanceStats() {
      const thisMonth = new Date().getMonth()
      const thisYear = new Date().getFullYear()

      const monthAttendances = this.attendances.filter(att => {
        const attDate = new Date(att.date)
        return attDate.getMonth() === thisMonth && attDate.getFullYear() === thisYear
      })

      this.attendanceStats.present = monthAttendances.filter(a => a.status === 'present').length
      this.attendanceStats.absent = monthAttendances.filter(a => a.status === 'absent').length
      this.attendanceStats.late = monthAttendances.filter(a => a.status === 'late').length

      const totalHours = monthAttendances.reduce((sum, att) => sum + (parseFloat(att.work_hours) || 0), 0)
      this.attendanceStats.avgHours = monthAttendances.length > 0
        ? (totalHours / monthAttendances.length).toFixed(1)
        : 0
    },
    openAttendanceModal() {
      this.attendanceForm = {
        id: null,
        date: new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Casablanca' }).format(new Date()),
        check_in: '',
        check_out: '',
        work_hours: 0,
        status: 'present',
        notes: ''
      }
      this.attendanceModal.show()
    },
    editAttendance(attendance) {
      this.attendanceForm = {
        id: attendance.id,
        date: attendance.date,
        check_in: attendance.check_in ? new Date(attendance.check_in).toTimeString().slice(0, 5) : '',
        check_out: attendance.check_out ? new Date(attendance.check_out).toTimeString().slice(0, 5) : '',
        work_hours: attendance.work_hours || 0,
        status: attendance.status,
        notes: attendance.notes || ''
      }
      this.attendanceModal.show()
    },
    calculateWorkHours() {
      if (this.attendanceForm.check_in && this.attendanceForm.check_out) {
        const checkIn = new Date(`2000-01-01T${this.attendanceForm.check_in}`)
        const checkOut = new Date(`2000-01-01T${this.attendanceForm.check_out}`)
        const diffMs = checkOut - checkIn
        const diffHrs = diffMs / (1000 * 60 * 60)
        this.attendanceForm.work_hours = diffHrs > 0 ? diffHrs.toFixed(2) : 0
      }
    },
    async saveAttendance() {
      try {
        if (!this.attendanceForm.date) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: this.$t('please_fill_required_fields')
          })
          return
        }

        const data = {
          employee_id: this.$route.params.id,
          date: this.attendanceForm.date,
          check_in: this.attendanceForm.check_in ? `${this.attendanceForm.date}T${this.attendanceForm.check_in}` : null,
          check_out: this.attendanceForm.check_out ? `${this.attendanceForm.date}T${this.attendanceForm.check_out}` : null,
          status: this.attendanceForm.status,
          notes: this.attendanceForm.notes
        }

        if (this.attendanceForm.id) {
          await helper.customPatch(`${process.env.VUE_APP_API}/employee-attendances/${this.attendanceForm.id}`, data)
        } else {
          await helper.customPost(`${process.env.VUE_APP_API}/employee-attendances`, data)
        }

        this.attendanceModal.hide()
        this.fetchAttendances()
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message || 'Failed to save attendance'
        })
      }
    },
    async deleteAttendance(attendance) {
      const result = await this.$swal({
        title: this.$t('are_you_sure'),
        text: this.$t('delete_attendance_record'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('yes_delete_it'),
        cancelButtonText: this.$t('cancel')
      })

      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/employee-attendances/${attendance.id}`)
          this.attendances = helper.withoutDeletedRecords(this.attendances, attendance.id)
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: error.message
          })
        }
      }
    },

    async fetchPayrolls() {
      try {
        this.payrollsLoading = true
        const response = await helper.customGet(`${process.env.VUE_APP_API}/payrolls?employee_id=${this.$route.params.id}&per_page=100`)
        this.payrolls = response.data.data || []
        this.payrollsLoading = false
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message
        })
        this.payrolls = []
        this.payrollsLoading = false
      }
    },
    formatPeriod(monthStr) {
      if (!monthStr) return '-'
      const [year, month] = monthStr.split('-')
      const date = new Date(year, month - 1)
      return date.toLocaleString('default', { month: 'long', year: 'numeric' })
    },
    openUserModal() {
      this.userForm = {
        name: this.employee.name,
        email: this.employee.email,
        password: '',
        role: 'staff'
      }
      this.permissionsState = {}
      // Default permissions for employee
      this.permissionsState['view_dashboard'] = true

      this.userModal.show()
    },
    toggleModulePermissions(module) {
      const allSelected = module.items.every(item =>
        Object.values(item)[0].permissions.every(p => this.permissionsState[p.key])
      )

      module.items.forEach(item => {
        Object.values(item)[0].permissions.forEach(p => {
          this.permissionsState[p.key] = !allSelected
        })
      })
    },
    toggleSubModulePermissions(subModule) {
      const allSelected = subModule.permissions.every(p => this.permissionsState[p.key])
      subModule.permissions.forEach(p => {
        this.permissionsState[p.key] = !allSelected
      })
    },
    async saveUser() {
      try {
        this.loading = true
        const permissions = Object.keys(this.permissionsState).filter(key => this.permissionsState[key])

        // 1. Create User
        const userResponse = await helper.customPost(`${process.env.VUE_APP_API}/users`, {
          ...this.userForm,
          permissions
        })

        const userId = userResponse.data.id

        // 2. Update Employee with attached_user_id
        await helper.customPatch(`${process.env.VUE_APP_API}/employees/${this.employee.id}`, {
          attached_user_id: userId
        })

        this.loading = false
        this.userModal.hide()
        this.fetchEmployee()
      } catch (error) {
        this.loading = false
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message || 'Failed to create user'
        })
      }
    }
  }
}
</script>

<style scoped>
.nav-link {
  cursor: pointer;
}

.card-header {
  border-bottom: 2px solid #f0f0f0;
}

.card-header h6 {
  color: #495057;
  font-weight: 600;
}
</style>
